import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PackageCheck, ShoppingCart, Loader2, BadgePercent, Send, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { cartAPI, notifyCartChanged, uploadsAPI, vehiclesAPI, promosAPI } from '../services/api';
import { promoSession } from '../services/savings';
import { PackShareDialog } from './PackShareDialog';

// Compare page: one-click "service all my vehicles" pack made of the parts common to the models (+ share it with a friend)
export const CommonPartsPack = ({ data, list, exact = true, sharedPromo = '' }) => {
  const names = data.vehicles.map((v) => `${v.brand} ${v.model}${v.engine ? ` (${v.engine})` : ''}`);
  const common = data.categories.flatMap((c) => c.common.map((p) => ({ ...p, category_name: c.name })));
  const [selected, setSelected] = useState(() => new Set(common.filter((p) => (p.stock ?? 1) > 0).map((p) => p.id)));
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  if (!common.length) return null;
  const picked = common.filter((p) => selected.has(p.id));
  const total = picked.reduce((n, p) => n + Number(p.price || 0), 0);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyPromo = async (code) => { const validated = await promosAPI.validate(code); promoSession.set(validated); return validated; };
  const addAll = async () => {
    if (!picked.length) return;
    setBusy(true);
    let ok = 0; const failed = [];
    for (const p of picked) {
      try { await cartAPI.addItem(p.id, 1); ok += 1; } catch (e) { failed.push(`${p.name}${e.response?.data?.detail || e.message ? ` (${e.response?.data?.detail || e.message})` : ''}`); }
    }
    notifyCartChanged();
    let promoMsg = '';
    if (ok && sharedPromo) {
      try { await applyPromo(sharedPromo); promoMsg = ` · code ami ${sharedPromo} appliqué (-1 %)`; } catch (e) { promoMsg = e.response?.status === 401 ? ' · connectez-vous pour utiliser le code ami' : ` · code ${sharedPromo} non applicable`; }
    } else if (ok === common.length) {
      try {
        const promo = await vehiclesAPI.packPromo(list, exact);
        await applyPromo(promo.code);
        promoMsg = ` · -${Number(promo.value).toFixed(0)} % pack appliqué (code ${promo.code})`;
      } catch (e) { if (e.response?.status === 401) promoMsg = ' · connectez-vous pour profiter de -1 % sur le pack'; }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} pièce(s) du pack ajoutée(s) au panier${promoMsg}`, { duration: 8000, action: { label: 'Voir le panier', onClick: () => { window.location.href = '/panier'; } } });
    if (failed.length) toast.error(`Non ajouté : ${failed.join(', ')}`);
  };
  return (
    <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 sm:p-5 mb-6" data-testid="common-parts-pack">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-bold text-emerald-900 flex items-center gap-2"><PackageCheck size={20} className="text-emerald-600" /> Pack « entretien de {data.vehicles.length > 2 ? 'mes trois' : 'mes deux'} véhicules »</h2>
          <p className="text-sm text-emerald-800">{common.length} pièce(s) conviennent à tous vos véhicules ({names.join(', ')}). Cochez celles à prévoir et ajoutez-les en un clic.</p>
          {sharedPromo ? (
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-white bg-orange-500 px-2.5 py-1 rounded-full" data-testid="common-parts-shared-promo"><Gift size={12} /> Code ami {sharedPromo} : -1 % appliqué dès l'ajout du pack au panier</p>
          ) : (
            <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 px-2.5 py-1 rounded-full" data-testid="common-parts-promo-hint"><BadgePercent size={12} /> -1 % sur le pack si vous prenez toutes les pièces communes{picked.length === common.length ? ' — remise active' : ` (${common.length - picked.length} manquante${common.length - picked.length > 1 ? 's' : ''})`}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-xs text-emerald-700">{picked.length} pièce(s) sélectionnée(s)</p>
          <p className="text-xl font-bold text-emerald-900" data-testid="common-parts-total">{total.toFixed(2)} €</p>
        </div>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {common.map((p) => (
          <li key={p.id} className={`flex items-center gap-3 bg-white rounded-lg border p-2 ${selected.has(p.id) ? 'border-emerald-300' : 'border-gray-200 opacity-70'}`} data-testid={`common-part-${p.id}`}>
            <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} disabled={(p.stock ?? 1) <= 0} className="w-4 h-4 accent-emerald-600" data-testid={`common-part-check-${p.id}`} aria-label={p.name} />
            {p.image && <img src={uploadsAPI.resolveUrl(p.image)} alt="" className="w-12 h-12 object-contain rounded no-placeholder" loading="lazy" />}
            <Link to={`/produit/${p.slug || p.id}`} className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-900 truncate hover:text-orange-600">{p.name}</span>
              <span className="block text-xs text-gray-500 truncate">{p.brand} · {p.category_name}{(p.stock ?? 1) <= 0 ? ' · rupture' : ''}</span>
            </Link>
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{Number(p.price || 0).toFixed(2)} €</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={addAll} disabled={busy || !picked.length} data-testid="common-parts-add" className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 px-5 py-2.5 rounded-full transition-colors">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />} Ajouter le pack au panier ({picked.length})
        </button>
        <button type="button" onClick={() => setSharing(true)} disabled={!picked.length} data-testid="common-parts-share" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 bg-white border border-emerald-300 hover:bg-emerald-100 disabled:opacity-50 px-4 py-2.5 rounded-full transition-colors">
          <Send size={15} /> Envoyer à un ami (il reçoit -1 %)
        </button>
      </div>
      {sharing && <PackShareDialog list={list} exact={exact} parts={picked} vehicles={data.vehicles} onClose={() => setSharing(false)} />}
    </section>
  );
};
